import { useEffect, useState } from "react";

// The server (marking_plans.php) is the only source of prices and script allowances;
// checkout always charges the server-side price.
export const PLAN_PERIODS = [
  { id: "month", label: "Monthly", suffix: "month", covers: "1 month", months: 1 },
  { id: "term", label: "Per term", suffix: "term", covers: "3 months", months: 3 },
  { id: "year", label: "Yearly", suffix: "year", covers: "12 months", months: 12 },
];

const periodOf = (period) => PLAN_PERIODS.find((item) => item.id === period) || PLAN_PERIODS[0];

// Term and yearly plans are shown as a monthly amount so the price reads like a normal monthly fee.
export const monthlyPriceCents = (plan) => Math.round(plan.price_cents / periodOf(plan.period).months);

// Every plan is a single Yoco charge; nothing is debited again automatically.
export const onceOffNote = (plan) => {
  const period = periodOf(plan.period);
  return period.months === 1
    ? "Once-off payment for 1 month. No debit order, no auto-renewal."
    : `Paid once: ${formatRand(plan.price_cents)} for ${period.covers}. No debit order, no auto-renewal.`;
};

export const TIER_TAGLINES = {
  basic: "For 1 class",
  teacher: "For 3–4 classes",
  pro: "For HODs & departments",
};

const TIER_EXTRAS = {
  basic: ["Unlimited classes and mark book", "Export to Excel"],
  teacher: ["Unlimited classes and mark book", "Export to Excel", "Advanced analytics"],
  pro: ["Unlimited classes and mark book", "Export to Excel", "Advanced analytics", "Priority support"],
};

export const formatRand = (cents) => `R${(cents / 100).toLocaleString("en-ZA", { maximumFractionDigits: 2 })}`;

// South African classes run 40-50 learners; plans are sized in class sets of 45.
const CLASS_SIZE = 45;

export function planFeatures(plan) {
  if (plan.scripts <= 0) return ["No AI-marked scripts included", ...(TIER_EXTRAS[plan.tier] || [])];
  const classSets = Math.round(plan.scripts / CLASS_SIZE);
  return [
    `${plan.scripts.toLocaleString("en-ZA")} AI-marked scripts per ${plan.period}`,
    `About ${classSets} class set${classSets === 1 ? "" : "s"} of ${CLASS_SIZE} learners`,
    ...(TIER_EXTRAS[plan.tier] || []),
  ];
}

export function usePlans(apiBase) {
  const [plans, setPlans] = useState([]);
  const [topup, setTopup] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    fetch(`${apiBase}/marking_plans.php`)
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
      .then((data) => {
        if (!active) return;
        setPlans(Array.isArray(data.plans) ? data.plans : []);
        setTopup(data.topup || null);
      })
      .catch(() => { if (active) setError("Plans could not be loaded. Please refresh the page."); });
    return () => { active = false; };
  }, [apiBase]);
  return { plans, topup, error };
}
